import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import SalesDetailsList from './components/SalesDetailsList';
import { IFilters } from './types';
import { downloadSalesDetails, fetchSalesDetails } from '../../api/sales-details';
import ContentWrapper from '../../components/ContentWrapper';
import EmptyData from '../../components/EmptyData';
import ProgressSection from '../../components/ProgressSection';
import { ISalesDetails } from '../../types/sales-details.type';
import { HttpUtils } from '../../utils/http.utils';
import SalesFilters from '../Sales/components/SalesFilters';

const SalesDetails = React.memo(() => {
  const [details, setDetails] = useState<ISalesDetails[]>([]);
  const [isSubmitProcess, setSubmitProcess] = useState<boolean>(false);
  const [isExportProcess, setExportProcess] = useState<boolean>(false);
  const [isReady, setReady] = useState<boolean>(false);

  const { t } = useTranslation();

  const handleSubmit = React.useCallback(
    (filters: IFilters) => {
      setSubmitProcess(true);

      if (!isReady) {
        setReady(true);
      }

      fetchSalesDetails(filters)
        .then((response) => setDetails(response))
        .catch(() => setDetails([]))
        .finally(() => setSubmitProcess(false));
    },
    [isReady],
  );

  const handleExport = React.useCallback(
    (filters: IFilters) => {
      setExportProcess(true);

      if (!isReady) {
        setReady(true);
      }

      downloadSalesDetails(filters)
        .then((response) => HttpUtils.handleBlob(response, 'sales_details.xlsx'))
        .finally(() => setExportProcess(false));
    },
    [isReady],
  );

  const renderList = React.useMemo(() => {
    if (!isReady) {
      return null;
    }

    if (isSubmitProcess) {
      return <ProgressSection mt={3} />;
    }

    if (!details.length) {
      return <EmptyData />;
    }

    return <SalesDetailsList details={details} />;
  }, [isReady, isSubmitProcess, details]);

  return (
    <ContentWrapper title={t('sales_details.title')}>
      <SalesFilters
        onSubmit={handleSubmit}
        onExport={handleExport}
        isSubmitProcess={isSubmitProcess}
        isExportProcess={isExportProcess}
      />

      {renderList}
    </ContentWrapper>
  );
});

export default SalesDetails;
